import React, { createContext, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import Header from '@/components/header/Header';
import Footer from '@/components/footer/Footer';
import TabNav from '@/components/TabNav/TabNav';
import Skeleton from 'react-loading-skeleton';
import find from 'lodash/find';
import t from '@/utils/Language/UI-translations';
import useAccount from '@/hooks/useAccount';
import useReAuth from '@/hooks/useReAuth';
import { useLoadCollection } from '@/hooks/useLoadCollection';
import { useCollectionPermissions } from '@/hooks/useCollectionPermissions';
import { toastContext } from '@/context/toast.context';

import CollectionSettings from '@/components/Collections/Settings/Settings';
import CollectionHeader from '@/components/Collections/Collection/CollectionHeader';

const CollectionPageContext = createContext(null);
export const useCollectionPage = () => useContext(CollectionPageContext);

const WithCollectionPageLayout = ({ children }) => {
  const [currentTab, setCurrentTab] = useState(null);
  const router = useRouter();
  const { addToast } = useContext(toastContext);
  const { account } = useAccount();
  const { collection, setCollection, loading, setLoading, loadCollection, loadedSlugRef } = useLoadCollection({ addToast, router });
  const { isOwner, isEditor } = useCollectionPermissions(collection, account?.data?.id);

  useReAuth();

  const handleTabChange = (tab) => {
    if (tab === 'collectionDetails' && router.query.categorySlug) {
      router.replace({ pathname: `/collections/${collection.slug}/` });
    } else {
      setCurrentTab(tab);
    }
  };

  const pageTabs = (tabKey) => {
    const tabs = [
      {
        title: t('Collection'),
        key: 'collectionDetails',
        Component: children
      }
    ];

    if (isOwner || isEditor) {
      tabs.push({
        title: t('Settings'),
        key: 'collectionSettings',
        Component: <CollectionSettings account={account} collection={collection} updateCollection={() => loadCollection({ slug: collection.slug })} />
      });
    }

    if (tabKey) {
      return find(tabs, (tab) => tab.key === tabKey) || tabs[0];
    }
    return tabs;
  };

  const contextValue = {
    account,
    collection,
    setCollection,
    loading,
    setLoading,
    loadCollection,
    loadedSlugRef,
    isOwner,
    isEditor
  };

  return (
    <CollectionPageContext.Provider value={contextValue}>
      <Header />
      <TabNav
        title={
          loading ? (
            <div>
              <Skeleton width={150} height={32} className="mb-2" />
              <Skeleton width={300} height={24} className="mb-2" />
              <Skeleton width={200} height={18} />
            </div>
          ) : (
            <CollectionHeader collection={collection} />
          )
        }
        tabs={pageTabs()}
        t={t}
        withBackButton={false}
        setCurrentTab={handleTabChange}
        currentTab={currentTab ? pageTabs(currentTab) : pageTabs()[0]}
      />
      <Footer />
    </CollectionPageContext.Provider>
  );
};

export default WithCollectionPageLayout;
